import React, { useState } from 'react';
import { ArrowRight, Compass } from 'lucide-react';
import { CommitteeInfo } from '../config/summitConfig';
import { CommitteeEmblem } from './CommitteeEmblems';

interface CommitteeCardProps {
  committee: CommitteeInfo;
  onExplore: (committee: CommitteeInfo) => void;
  reducedMotion: boolean;
}

export const CommitteeCard: React.FC<CommitteeCardProps> = ({
  committee,
  onExplore,
  reducedMotion,
}) => {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reducedMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const tiltX = -((y / rect.height) - 0.5) * 8;
    const tiltY = ((x / rect.width) - 0.5) * 8;
    setTilt({ x: tiltX, y: tiltY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  return (
    <div
      onClick={() => onExplore(committee)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="marble-card rounded-sm p-6 sm:p-7 flex flex-col justify-between cursor-pointer transition-all duration-300 group preserve-3d relative overflow-hidden"
      style={{
        transform: reducedMotion ? 'none' : `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateZ(5px)`,
      }}
      data-cursor-interactive
      data-cursor-text="ARENA"
    >
      {/* Background Centered Committee Emblem Watermark - Fills the Box */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden p-2 sm:p-3"
        aria-hidden="true"
      >
        <div className="w-[90%] h-[90%] max-w-[340px] max-h-[340px] opacity-[0.18] group-hover:opacity-[0.40] text-bronze group-hover:text-beige transition-all duration-700 ease-out transform group-hover:scale-108 filter drop-shadow-[0_0_25px_rgba(212,175,55,0.20)] group-hover:drop-shadow-[0_0_40px_rgba(212,175,55,0.45)] flex items-center justify-center">
          <CommitteeEmblem id={committee.id} />
        </div>
      </div>

      {/* Subtle Chamber Number Watermark */}
      <div className="absolute right-4 bottom-16 text-white/[0.04] group-hover:text-white/[0.08] transition-colors select-none font-mono text-[10px] tracking-widest pointer-events-none z-10">
        CHAMBER • {committee.number}
      </div>

      <div className="relative z-10">
        {/* Top Header: Number and Category Badge */}
        <div className="flex items-center justify-between mb-5 border-b border-white/5 pb-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-bold text-bronze tracking-widest">
              {committee.number}
            </span>
            <span className="text-[10px] text-ivory/40 uppercase tracking-widest">
              CHAMBER
            </span>
          </div>
          <span className="text-[9px] uppercase tracking-widest px-2.5 py-1 bg-white/5 border border-bronze/30 text-beige-300 rounded-sm">
            {committee.category}
          </span>
        </div>

        {/* Committee Abbreviation & Full Name */}
        <h3 className="font-serif text-2xl sm:text-3xl font-bold tracking-wide text-ivory group-hover:text-beige transition-colors mb-2 leading-tight">
          {committee.abbreviation}
        </h3>
        {committee.fullName && 
         committee.fullName.toLowerCase() !== committee.name.toLowerCase() && 
         committee.fullName.toLowerCase() !== committee.abbreviation.toLowerCase() && (
          <h4 className="text-xs uppercase tracking-widest text-beige-400 font-semibold mb-4 leading-relaxed">
            {committee.fullName}
          </h4>
        )}

        {/* Description */}
        <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
          {committee.description}
        </p>
      </div>

      {/* Card Footer: EXPLORE → */}
      <div className="relative z-10 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-beige tracking-widest uppercase font-semibold">
        <span className="group-hover:text-ivory transition-colors">EXPLORE</span>
        <div className="w-8 h-8 rounded-full border border-bronze/40 group-hover:border-beige flex items-center justify-center group-hover:bg-beige group-hover:text-deepblack transition-all">
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </div>
  );
};
